import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from 'react'
import { getApiTargetInfo, yesNoYNMap } from 'core'

import { connect } from 'react-redux'
import CommonScreen from 'admin/component/CommonScreen'

import {
  getSolarPlant,
  deleteSolarPlant,
  getSolarEntity,
  getSolartimeZone,
  getSolarCountryList,
  getSolarStateList,
  getSolarRegion,
  getSolarRegim,
  getSolarOwnershipStatus,
  saveSolarPlant,
  getCountryProvinceList,
  renameSolarPlant,
} from 'api/pgads'

import { ErrorContext } from 'context'
import R from 'ramda'

import { toSelectionColProps } from '@integec/grid-tools'
import { SolarPlantHeaders } from 'solar/Headers'
import { Button, DatePicker, Input, InputNumber, Select } from 'antd'
import { HistoryModal, RenameModal } from './SolarStorageGroup'

const title = 'Solar Plant'

const getMap = (data, key, value) => {
  return R.compose(
    R.fromPairs,
    R.map(d => [d[key], d[value]])
  )(data)
}

const SolarPlant = ({ apiTargetInfo }) => {
  const [insertEvent, setInsertEvent] = useState(false)
  const [availableStatus, setAvailableStatus] = useState({})
  const [regionMap, setRegionMap] = useState({})
  const [countryMap, setCountryMap] = useState({})
  const [entityMap, setEntityMap] = useState({})
  const [stateProvMap, setStateProvMap] = useState({})
  const [timeZoneMap, setTimeZoneMap] = useState({})
  const [regimMap, setRegimMap] = useState({})
  const [countryProvMap, setCountryProvMap] = useState({})
  const [showRenameModal, setShowRenameModal] = useState(false)
  const [showHistoryModal, setShowHistoryModal] = useState(false)

  const { setError } = useContext(ErrorContext)

  useEffect(
    _ => {
      getSolarEntity(apiTargetInfo).fork(setError, data => {
        const cd = R.compose(
          R.fromPairs,
          R.map(d => [d.entityId, d.entityId + ' - ' + d.entityName])
        )(data)
        // setEntityMap(getMap(data, 'entityId', 'entityName'))
        setEntityMap(cd)
      })
      getSolartimeZone(apiTargetInfo).fork(setError, data => {
        setTimeZoneMap(getMap(data, 'timeZoneCode', 'timeZoneName'))
      })
      getSolarCountryList(apiTargetInfo).fork(setError, data => {
        setCountryMap(getMap(data, 'countryId', 'countryName'))
      })
      getSolarStateList(apiTargetInfo).fork(setError, data => {
        setStateProvMap(getMap(data, 'state', 'stateName'))
      })
      getCountryProvinceList(apiTargetInfo).fork(setError, data => {
        setCountryProvMap(getMap(data, 'state', 'stateName'))
      })
      getSolarRegion(apiTargetInfo).fork(setError, data => {
        setRegionMap(getMap(data, 'regionCode', 'regionName'))
      })
      getSolarRegim(apiTargetInfo).fork(setError, data => {
        setRegimMap(getMap(data, 'solarRegimeCode', 'solarRegimeName'))
      })

      getSolarOwnershipStatus(apiTargetInfo).fork(setError, data => {
        setAvailableStatus(
          getMap(data, 'ownershipStatusCode', 'ownershipStatusName')
        )
      })
    },
    [apiTargetInfo, setError]
  )

  const headerWithformater = useMemo(
    _ => {
      return SolarPlantHeaders.map(header => {
        if (header.ident === 'plantOwnershipStatus')
          return {
            ...header,
            dataFormatter: ({ value }) =>
              availableStatus[value]
                ? value + '-' + availableStatus[value]
                : value,
            sortIndexGetter: ({ rowData }) =>
              availableStatus[rowData.plantOwnershipStatus] ||
              rowData.plantOwnershipStatus,
            ...toSelectionColProps(availableStatus),
          }
        if (header.ident === 'region')
          return {
            ...header,
            dataFormatter: ({ value }) =>
              regionMap[value] ? value + '-' + regionMap[value] : value,
            sortIndexGetter: ({ rowData }) =>
              regionMap[rowData.region] || rowData.region,
            ...toSelectionColProps(regionMap),
          }
        if (header.ident === 'entityId')
          return {
            ...header,
            dataFormatter: ({ value }) => entityMap[value] || value,
            sortIndexGetter: ({ rowData }) =>
              entityMap[rowData.entityId] || rowData.entityId,
            ellipsis: true,
          }
        if (header.ident === 'availableStatus')
          return {
            ...header,
            dataFormatter: ({ value }) =>
              availableStatus[value]
                ? value + '-' + availableStatus[value]
                : value,
            sortIndexGetter: ({ rowData }) =>
              availableStatus[rowData.availableStatus] ||
              rowData.availableStatus,
            ...toSelectionColProps(availableStatus),
          }
        if (header.ident === 'country')
          return {
            ...header,
            dataFormatter: ({ value }) =>
              countryMap[value] ? value + '-' + countryMap[value] : value,
            sortIndexGetter: ({ rowData }) =>
              countryMap[rowData.country] || rowData.country,
            ...toSelectionColProps(countryMap),
          }
        if (header.ident === 'stateProvince')
          return {
            ...header,
            dataFormatter: ({ value, rowData }) => {
              if (rowData.country === 'CA')
                return countryProvMap[value] || value
              else return stateProvMap[value] || value
            },
            sortIndexGetter: ({ rowData }) =>
              stateProvMap[rowData.stateProvince] || rowData.stateProvince,
            ...toSelectionColProps({ ...stateProvMap, ...countryProvMap }),
          }
        if (header.ident === 'timeZone')
          return {
            ...header,
            dataFormatter: ({ value }) =>
              timeZoneMap[value] ? value + '-' + timeZoneMap[value] : value,
            sortIndexGetter: ({ rowData }) =>
              timeZoneMap[rowData.timeZone] || rowData.timeZone,
            ...toSelectionColProps(timeZoneMap),
          }
        if (header.ident === 'onSiteStorage')
          return {
            ...header,
            dataFormatter: ({ value }) =>
              yesNoYNMap[value] ? value + '-' + yesNoYNMap[value] : value,
            sortIndexGetter: ({ rowData }) =>
              yesNoYNMap[rowData.onSiteStorage] || rowData.onSiteStorage,
            ...toSelectionColProps(yesNoYNMap),
          }
        if (header.ident === 'daylightSaving')
          return {
            ...header,
            dataFormatter: ({ value }) =>
              yesNoYNMap[value] ? value + '-' + yesNoYNMap[value] : value,
            sortIndexGetter: ({ rowData }) =>
              yesNoYNMap[rowData.daylightSaving] || rowData.daylightSaving,
            ...toSelectionColProps(yesNoYNMap),
          }
        if (header.ident === 'solarRegime')
          return {
            ...header,
            dataFormatter: ({ value }) =>
              regimMap[value] ? value + '-' + regimMap[value] : value,
            sortIndexGetter: ({ rowData }) =>
              regimMap[rowData.solarRegime] || rowData.solarRegime,
            ...toSelectionColProps(regimMap),
          }

        return header
      })
    },
    [
      availableStatus,
      countryMap,
      stateProvMap,
      timeZoneMap,
      entityMap,
      regionMap,
      regimMap,
      countryProvMap,
    ]
  )

  const InsertComp = comProps => (
    <SolarPlantModal
      {...comProps}
      insertEvent={insertEvent}
      setInsertEvent={setInsertEvent}
      availableStatus={availableStatus}
      countryMap={countryMap}
      stateProvMap={stateProvMap}
      countryProvMap={countryProvMap}
      timeZoneMap={timeZoneMap}
      entityMap={entityMap}
      regionMap={regionMap}
      regimMap={regimMap}
    />
  )

  const extraCriteriaAfterButton = ({
    data,
    onRetrieve,
    apiTargetInfo,
    setSpinning,
  }) => {
    return (
      <div>
        {showRenameModal && (
          <RenameModal
            list={data.map(e => ({
              label: e.plantId,
              value: e.plantId + '-' + e.entityId,
            }))}
            modalFor="Plant"
            hide={() => setShowRenameModal(false)}
            onOk={data => {
              const { oldId, newId } = data
              const ids = oldId?.split('-')

              setSpinning(true)
              renameSolarPlant(apiTargetInfo)({
                oldId: ids[0],
                entityId: ids[1],
                newId,
              }).fork(setError, _ => {
                setSpinning(false)
                onRetrieve()
              })
            }}
          />
        )}
        {showHistoryModal && (
          <HistoryModal
            dataName="PLANT"
            apiTargetInfo={apiTargetInfo}
            setError={setError}
            hide={() => setShowHistoryModal(false)}
          />
        )}
        <Button
          onClick={() => setShowRenameModal(true)}
          type="primary"
          // style={{ marginLeft: '10px' }}
        >
          Rename Plant
        </Button>
        <Button
          onClick={() => setShowHistoryModal(true)}
          type="primary"
          style={{ marginLeft: '10px' }}
        >
          Show Rename History
        </Button>
      </div>
    )
  }

  return (
    <CommonScreen
      title={title}
      header={headerWithformater}
      saveTask={saveSolarPlant}
      retrieveTask={getSolarPlant}
      deleteTask={deleteSolarPlant}
      altBy={d => d.entityId}
      autoRetrieve
      showDatePickerOnEdit
      //   sortOptions={[
      //     strCol({
      //       ident: 'plantName',
      //       display: 'Plant Name',
      //       isKey: true,
      //       sortOrder: 'asc',
      //     }),
      //   ]}
      //   initialSortOptions={[
      //     strCol({
      //       ident: 'plantName',
      //       display: 'Plant Name',
      //       isKey: true,
      //       sortOrder: 'asc',
      //     }),
      //   ]}
      widthOffset={50}
      extraCriteriaAfterButton={extraCriteriaAfterButton}
      insertComp={insertEvent && InsertComp}
      insertCompHeight={200}
      onInsertEvent={b => setInsertEvent(b)}
      autoFixColByKey={false}
      headerRowHeight={60}
      defaultInsertData={{ daylightSaving: 'N', plantOwnershipStatus: 'AV' }}
    />
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
})

export default connect(mapStateToProps)(SolarPlant)

const SolarPlantModal = ({
  setInsertData,
  insertData,
  onAdd,
  onCancel,
  // setInsertEvent,
  // insertEvent,
  availableStatus = {},
  countryMap = {},
  stateProvMap = {},
  timeZoneMap = {},
  entityMap = {},
  regionMap = {},
  regimMap = {},
  countryProvMap = {},
}) => {
  const getLabel = useCallback((key, isKey, isRequired) => {
    return (
      <div>
        {isKey && <span style={{ color: 'red', marginRight: '2px' }}>*</span>}
        {!isKey && isRequired && (
          <span style={{ color: 'red', marginRight: '2px' }}>*</span>
        )}
        {(SolarPlantHeaders.find(f => f.ident === key) &&
          SolarPlantHeaders.find(f => f.ident === key).display) ||
          ''}{' '}
        :
      </div>
    )
  }, [])

  const getInput = useCallback(
    ({
      key,
      maxLength,
      isKey,
      insertData,
      setInsertData,
      isRequired = false,
    }) => (
      <div>
        <span style={{ display: 'flex' }}>
          {getLabel(key, isKey, isRequired)}
        </span>
        <Input
          disabled={insertData.insert && isKey}
          className={key}
          data-cy={key}
          value={insertData[key]}
          onChange={e =>
            setInsertData({ ...insertData, [key]: e.target.value })
          }
          maxLength={maxLength}
        />
      </div>
    ),
    [getLabel]
  )
  const getMapSelect = useCallback(
    ({
      key,
      keymap,
      getKeyMap,
      isKey,
      setInsertData,
      insertData,
      isRequired = false,
      extStyle = {},
    }) => (
      <div style={{ ...extStyle }} className={key}>
        <span style={{ display: 'flex' }}>
          {getLabel(key, isKey, isRequired)}
        </span>
        <Select
          showSearch
          disabled={insertData.insert && isKey}
          className={key}
          data-cy={key}
          style={{ width: '100%', zIndex: 500 }}
          value={insertData[key]}
          onChange={val => setInsertData({ ...insertData, [key]: val })}
          filterOption={(input, option) =>
            option.props.children.toLowerCase().indexOf(input.toLowerCase()) >=
            0
          }
        >
          {Object.keys(getKeyMap ? getKeyMap() : keymap).map(k => (
            <Select.Option value={k} key={k} className={key} data-cy={key}>
              {/* {`${k}-${keymap[k]}`} */}
              {`${getKeyMap ? getKeyMap()[k] : keymap[k]}`}
            </Select.Option>
          ))}
        </Select>
      </div>
    ),
    [getLabel]
  )

  const getInputNumber = useCallback(
    ({
      key,
      precision = 0,
      isKey = false,
      insertData,
      setInsertData,
      isRequired = false,
    }) => (
      <div>
        <span style={{ display: 'flex' }}>
          {getLabel(key, isKey, isRequired)}
        </span>
        <InputNumber
          disabled={insertData.insert && isKey}
          className={key}
          data-cy={key}
          style={{ width: '100%' }}
          value={insertData[key]}
          onChange={value =>
            setInsertData({ ...insertData, [key]: value + '' })
          }
          precision={precision}
        />
      </div>
    ),
    [getLabel]
  )
  const getDate = useCallback(
    ({ key, insertData, isKey, setInsertData, isRequired = false }) => (
      <div>
        {getLabel(key, isKey, isRequired)}
        <DatePicker
          style={{ width: '100%' }}
          className={key}
          data-cy={key}
          format="MM/DD/YYYY"
          value={insertData[key + 'date']}
          onChange={d => {
            setInsertData({
              ...insertData,
              [key]: d.format('YYYY-MM-DD'),
              [key + 'date']: d,
            })
          }}
        />
      </div>
    ),
    [getLabel]
  )

  return (
    <div
      style={{
        display: 'grid',
        // gridTemplateColumns: '230px 450px 230px 230px',
        gridTemplateColumns: 'repeat(4, 285px)',
        gridTemplateRows: '60px 60px 60px',
        gridColumnGap: '10px',
        gridRowGap: '7px',
      }}
    >
      {/* {getInput({
        setInsertData,
        insertData,
        key: 'entityId',
        isKey: true,
      })} */}
      {getMapSelect({
        setInsertData,
        insertData,
        key: 'entityId',
        isKey: true,
        keymap: entityMap,
      })}
      {getMapSelect({
        setInsertData,
        insertData,
        key: 'region',
        isKey: true,
        keymap: regionMap,
        maxLength: 8,
      })}
      {getInput({
        setInsertData,
        insertData,
        key: 'plantId',
        isKey: true,
      })}
      {getInput({
        setInsertData,
        insertData,
        key: 'plantName',
        // isKey: true,
        isRequired: true,
      })}
      {getInput({
        setInsertData,
        insertData,
        key: 'eiaPlantCode',
        isKey: true,
        maxLength: 6,
      })}

      {getMapSelect({
        setInsertData,
        insertData,
        key: 'plantOwnershipStatus',
        isKey: true,
        keymap: availableStatus,
      })}
      {getMapSelect({
        setInsertData,
        insertData,
        key: 'timeZone',
        keymap: timeZoneMap,
        isRequired: true,
      })}
      {getMapSelect({
        setInsertData,
        insertData,
        key: 'daylightSaving',
        keymap: { Y: 'Yes', N: 'No' },
        isRequired: true,
      })}
      {getMapSelect({
        setInsertData,
        insertData,
        key: 'solarRegime',
        keymap: regimMap,
        isRequired: true,
      })}
      {getInput({
        setInsertData,
        insertData,
        key: 'isoResourceId',
      })}
      {getDate({
        setInsertData,
        insertData,
        key: 'effectiveDate',
        maxLength: 10,
        isRequired: true,
      })}

      {getMapSelect({
        setInsertData,
        insertData,
        key: 'country',
        keymap: countryMap,
        maxLength: 2,
        isRequired: true,
      })}
      {getMapSelect({
        setInsertData,
        insertData,
        key: 'stateProvince',
        keymap: stateProvMap,
        getKeyMap: _ => {
          return insertData.country === 'CA' ? countryProvMap : stateProvMap
        },
        maxLength: 2,
        isRequired: true,
      })}
      {getInput({
        setInsertData,
        insertData,
        key: 'nearestCity',
        isRequired: true,
      })}
      {getInputNumber({
        setInsertData,
        insertData,
        key: 'elevationM',
        isRequired: true,
      })}

      {getInputNumber({
        setInsertData,
        insertData,
        key: 'globalHIrradiance',
        isRequired: true,
        precision: 2,
      })}
      {getInput({
        setInsertData,
        insertData,
        key: 'latitude',
        isRequired: true,
      })}
      {getInput({
        setInsertData,
        insertData,
        key: 'longitude',
        isRequired: true,
      })}

      {getInputNumber({
        setInsertData,
        insertData,
        key: 'interAnnualVarIrr',
        isRequired: true,
        precision: 2,
      })}
      {getMapSelect({
        setInsertData,
        insertData,
        key: 'onSiteStorage',
        keymap: { Y: 'Yes', N: 'No' },
        isRequired: true,
      })}
      {getInputNumber({
        setInsertData,
        insertData,
        key: 'plantCapPoi',
        isRequired: true,
      })}
      {getInput({
        setInsertData,
        insertData,
        key: 'transferToEntityId',
        maxLength: 10,
      })}
      <div
        style={{
          gridColumnEnd: 'span 3',
          display: 'grid',
          gridTemplateColumns: 'min-content max-content min-content',
          gap: 10,
          // paddingBottom: '7px',
          alignContent: 'end',
        }}
      >
        <Button type="primary" onClick={() => onAdd(true)}>
          Add
        </Button>

        <Button type="primary" onClick={() => onAdd(false)}>
          Add & Hide
        </Button>
        <Button type="danger" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
